import {useTransactionByIdLazyQuery} from '@transactions/graphql';
import {ChevronDown} from 'lucide-react';
import * as React from 'react';
import {ConsumptionsList} from 'src/ConsumptionsList';
import {cn} from 'src/lib/utils';
import Loading from 'src/Loading';

/**
 * Under a transaction made of several lines: a toggle that opens those
 * lines in place, each linking to its own page. The lines load when opened
 * (the list's fragment carries only their ids), through the same query the
 * transaction's page uses — kept out of the shared cache: written there,
 * it would replace the list's transaction object (dropping a selection held
 * by reference), and a later list write would strip the lines back out.
 *
 * It sits below the row rather than inside it because the whole row is a
 * link, and a control inside a link is not a control a screen reader or a
 * keyboard can reach on its own.
 */
export const TransactionLines = ({
  transactionId,
  lineCount,
  currency,
}: {
  transactionId: string;
  lineCount: number;
  currency: string;
}) => {
  const [open, setOpen] = React.useState(false);
  const [load, {data, loading, error, refetch}] = useTransactionByIdLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const panelId = React.useId();

  const toggle = () => {
    if (!open) {
      // Fresh on every open: lines edited since the last one show as they are.
      if (data == null) void load({variables: {id: transactionId}});
      else void refetch();
    }
    setOpen(!open);
  };

  const consumptions = data?.transactionById?.consumptions ?? null;

  return (
    <div className="border-border border-b">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 pb-2 pl-16 text-xs max-md:pl-[54px]"
      >
        {lineCount} lines
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div id={panelId} className="bg-muted/30 border-border border-t pl-12 text-sm max-md:pl-10">
          {consumptions != null ? (
            <ConsumptionsList consumptions={consumptions} currency={currency} showTotal={false} />
          ) : error ? (
            <div className="text-muted-foreground px-4 py-3">Couldn&apos;t load its lines.</div>
          ) : loading ? (
            <div className="flex justify-center py-3">
              <Loading size="small" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
